/**
 * User Stickies Store
 *
 * Zustand store for managing user-created sticky notes.
 * These stickies appear on ALL /app pages, unlike Goals/Focus which are dashboard-only.
 *
 * Features:
 * - CRUD operations for stickies
 * - Position and collapsed state management
 * - Color cycling for new stickies
 */

import { create } from 'zustand';
import type {
  UserStickyNote,
  StickyPosition,
  StickySize,
  StickyColor,
} from '@shared/types/userStickies';
import {
  stickyColorOrder,
  DEFAULT_STICKY_POSITION,
  DEFAULT_STICKY_SIZE,
  STICKY_POSITION_OFFSET,
  DEFAULT_STICKY_TITLE,
} from '@shared/types/userStickies';

/**
 * Dock display modes:
 * - 'full': Shows horizontal pills with icons + titles + chevron
 * - 'menuOnly': Shows only menu button + chevron (pills hidden)
 * - 'hidden': Everything hidden, access via drawer only
 */
export type DockDisplayMode = 'full' | 'menuOnly' | 'hidden';

interface UserStickiesState {
  // Data
  stickies: UserStickyNote[];

  // Loading state
  isLoading: boolean;
  isInitialized: boolean;

  // Delete confirmation modal state
  deleteModalOpen: boolean;
  stickyToDelete: string | null;

  // Dock display mode (replaces simple collapsed boolean)
  dockDisplayMode: DockDisplayMode;

  // Legacy: isDockCollapsed (for backwards compatibility, maps to dockDisplayMode)
  isDockCollapsed: boolean;

  // Actions - CRUD
  addSticky: () => string; // Returns new sticky ID
  updateSticky: (id: string, updates: Partial<Omit<UserStickyNote, 'id' | 'createdAt'>>) => void;
  deleteSticky: (id: string) => void;

  // Actions - Content
  setContent: (id: string, content: string) => void;
  setTitle: (id: string, title: string) => void;

  // Actions - State
  toggleCollapsed: (id: string) => void;
  setCollapsed: (id: string, collapsed: boolean) => void;
  setPosition: (id: string, position: StickyPosition) => void;
  setSize: (id: string, size: StickySize) => void;

  // Actions - Delete Modal
  openDeleteModal: (id: string) => void;
  closeDeleteModal: () => void;
  confirmDelete: () => void;

  // Actions - Dock Display Mode
  setDockDisplayMode: (mode: DockDisplayMode) => void;
  cycleDockDisplayMode: () => void;  // Cycles: full -> menuOnly -> hidden -> full

  // Legacy actions (for backwards compatibility)
  setDockCollapsed: (collapsed: boolean) => void;
  toggleDockCollapsed: () => void;

  // Actions - Bulk state
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  hydrate: (stickies: UserStickyNote[]) => void;
  reset: () => void;
}

/**
 * Generate UUID for new stickies
 */
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Get next color based on existing stickies count
 */
const getNextColor = (stickies: UserStickyNote[]): StickyColor => {
  return stickyColorOrder[stickies.length % stickyColorOrder.length];
};

/**
 * Get position for new sticky (staggered from existing ones)
 */
const getNextPosition = (stickies: UserStickyNote[]): StickyPosition => {
  const offset = stickies.length * STICKY_POSITION_OFFSET;
  return {
    x: DEFAULT_STICKY_POSITION.x + offset,
    y: DEFAULT_STICKY_POSITION.y + offset,
  };
};

/**
 * Initial state
 */
const initialState = {
  stickies: [],
  isLoading: false,
  isInitialized: false,
  deleteModalOpen: false,
  stickyToDelete: null,
  dockDisplayMode: 'full' as DockDisplayMode,
  isDockCollapsed: false,  // Legacy: derived from dockDisplayMode === 'hidden'
};

/**
 * User Stickies Zustand Store
 */
export const useUserStickiesStore = create<UserStickiesState>((set, get) => ({
  ...initialState,

  // CRUD Actions
  addSticky: () => {
    const { stickies } = get();
    const id = generateId();
    const now = Date.now();

    const newSticky: UserStickyNote = {
      id,
      title: DEFAULT_STICKY_TITLE,
      content: '',
      isCollapsed: false,
      position: getNextPosition(stickies),
      size: { ...DEFAULT_STICKY_SIZE },
      color: getNextColor(stickies),
      createdAt: now,
      updatedAt: now,
    };

    set({ stickies: [...stickies, newSticky] });
    return id;
  },

  updateSticky: (id, updates) => {
    set((state) => ({
      stickies: state.stickies.map((sticky) =>
        sticky.id === id
          ? { ...sticky, ...updates, updatedAt: Date.now() }
          : sticky
      ),
    }));
  },

  deleteSticky: (id) => {
    set((state) => ({
      stickies: state.stickies.filter((sticky) => sticky.id !== id),
    }));
  },

  // Content Actions
  setContent: (id, content) => {
    set((state) => ({
      stickies: state.stickies.map((sticky) =>
        sticky.id === id
          ? { ...sticky, content, updatedAt: Date.now() }
          : sticky
      ),
    }));
  },

  setTitle: (id, title) => {
    set((state) => ({
      stickies: state.stickies.map((sticky) =>
        sticky.id === id
          ? { ...sticky, title, updatedAt: Date.now() }
          : sticky
      ),
    }));
  },

  // State Actions
  toggleCollapsed: (id) => {
    set((state) => ({
      stickies: state.stickies.map((sticky) =>
        sticky.id === id
          ? { ...sticky, isCollapsed: !sticky.isCollapsed, updatedAt: Date.now() }
          : sticky
      ),
    }));
  },

  setCollapsed: (id, collapsed) => {
    set((state) => ({
      stickies: state.stickies.map((sticky) =>
        sticky.id === id
          ? { ...sticky, isCollapsed: collapsed, updatedAt: Date.now() }
          : sticky
      ),
    }));
  },

  setPosition: (id, position) => {
    set((state) => ({
      stickies: state.stickies.map((sticky) =>
        sticky.id === id
          ? { ...sticky, position, updatedAt: Date.now() }
          : sticky
      ),
    }));
  },

  setSize: (id, size) => {
    set((state) => ({
      stickies: state.stickies.map((sticky) =>
        sticky.id === id
          ? { ...sticky, size, updatedAt: Date.now() }
          : sticky
      ),
    }));
  },

  // Delete Modal Actions
  openDeleteModal: (id) => {
    set({ deleteModalOpen: true, stickyToDelete: id });
  },

  closeDeleteModal: () => {
    set({ deleteModalOpen: false, stickyToDelete: null });
  },

  confirmDelete: () => {
    const { stickyToDelete } = get();
    if (stickyToDelete) {
      get().deleteSticky(stickyToDelete);
    }
    set({ deleteModalOpen: false, stickyToDelete: null });
  },

  // Dock Display Mode Actions
  setDockDisplayMode: (mode) => set({
    dockDisplayMode: mode,
    isDockCollapsed: mode === 'hidden',  // Legacy compatibility
  }),

  cycleDockDisplayMode: () => set((state) => {
    // Cycle: full -> menuOnly -> hidden -> full
    const nextMode: DockDisplayMode =
      state.dockDisplayMode === 'full' ? 'menuOnly' :
      state.dockDisplayMode === 'menuOnly' ? 'hidden' : 'full';
    return {
      dockDisplayMode: nextMode,
      isDockCollapsed: nextMode === 'hidden',  // Legacy compatibility
    };
  }),

  // Legacy Dock Collapse Actions (for backwards compatibility)
  setDockCollapsed: (collapsed) => set({
    isDockCollapsed: collapsed,
    dockDisplayMode: collapsed ? 'hidden' : 'full',
  }),
  toggleDockCollapsed: () => set((state) => {
    const newCollapsed = !state.isDockCollapsed;
    return {
      isDockCollapsed: newCollapsed,
      dockDisplayMode: newCollapsed ? 'hidden' : 'full',
    };
  }),

  // Bulk State Actions
  setLoading: (loading) => set({ isLoading: loading }),
  setInitialized: (initialized) => set({ isInitialized: initialized }),

  hydrate: (stickies) =>
    set({
      stickies,
      isInitialized: true,
    }),

  reset: () => set(initialState),
}));
